// Authenticated header for control panel
// Requirements: 5.1, 5.2

import React, { useState, useRef, useEffect } from 'react';
import { User } from '../../types/auth';
import ApiStatus from '../common/ApiStatus';
import OptinodeAILogo from '../common/OptinodeAILogo';
import './AuthenticatedHeader.css';

interface AuthenticatedHeaderProps {
  user: User;
  onLogout: () => void;
  onToggleSidebar: () => void;
  onToggleAIAssistant: () => void;
  isAIAssistantVisible: boolean;
  isSidebarCollapsed: boolean;
  onNavigateHome?: () => void;
}

export const AuthenticatedHeader: React.FC<AuthenticatedHeaderProps> = ({
  user,
  onLogout,
  onToggleSidebar,
  onToggleAIAssistant,
  isAIAssistantVisible,
  isSidebarCollapsed,
  onNavigateHome
}) => {
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isProfileDropdownOpen]);

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const handleLogout = () => {
    setIsProfileDropdownOpen(false);
    onLogout();
  };

  const getUserDisplayName = () => {
    if (user.profile?.firstName && user.profile?.lastName) {
      return `${user.profile.firstName} ${user.profile.lastName}`;
    }
    return user.email;
  };

  const getUserInitials = () => {
    if (user.profile?.firstName && user.profile?.lastName) {
      return `${user.profile.firstName[0]}${user.profile.lastName[0]}`.toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  return (
    <header className="authenticated-header" role="banner">
      <div className="header-container">
        {/* Left Section - Hamburger */}
        <div className="header-left">
          <button
            className="sidebar-toggle-btn"
            onClick={onToggleSidebar}
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className="hamburger-icon">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>

        {/* Center Section - Logo */}
        <div className="header-center">
          {onNavigateHome && (
            <button
              className="header-logo-btn"
              onClick={onNavigateHome}
              aria-label="Navigate to home page"
              title="Go to home page"
            >
              <OptinodeAILogo width={32} height={32} className="logo-icon" />
              <span className="logo-text">Optinode AI</span>
            </button>
          )}
        </div>

        {/* Right Section - AI Assistant and User */}
        <div className="header-right">
          <div className="header-actions">
            {/* AI Assistant Toggle */}
            <button
              className={`ai-assistant-btn ${isAIAssistantVisible ? 'active' : ''}`}
              onClick={onToggleAIAssistant}
              aria-label={isAIAssistantVisible ? 'Close AI Assistant' : 'Open AI Assistant'}
              title={isAIAssistantVisible ? 'Close AI Assistant' : 'Open AI Assistant'}
            >
              <span className="ai-icon">🤖</span>
              <span className="ai-text">AI Assistant</span>
            </button>

            {/* API Status */}
            <div className="api-status-container">
              <ApiStatus variant="pill" />
            </div>

            {/* User Profile Dropdown */}
            <div className="user-profile-dropdown" ref={dropdownRef}>
              <button
                className={`profile-trigger ${isProfileDropdownOpen ? 'active' : ''}`}
                onClick={toggleProfileDropdown}
                aria-expanded={isProfileDropdownOpen}
                aria-haspopup="true"
                aria-label="User profile menu"
              >
                <div className="profile-avatar">
                  <span className="avatar-text">{getUserInitials()}</span>
                </div>
                <div className="profile-info">
                  <span className="profile-name">{getUserDisplayName()}</span>
                  <span className="profile-role">{user.role || 'User'}</span>
                </div>
                <span className="dropdown-arrow">▼</span>
              </button>

              {isProfileDropdownOpen && (
                <div className="profile-dropdown-menu" role="menu">
                  <div className="dropdown-header">
                    <div className="user-details">
                      <div className="user-avatar-large">
                        <span className="avatar-text-large">{getUserInitials()}</span>
                      </div>
                      <div className="user-info">
                        <span className="user-name">{getUserDisplayName()}</span>
                        <span className="user-email">{user.email}</span>
                        <span className="user-tenant-info">
                          {user.tenant?.name || 'Default Tenant'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="dropdown-divider" />

                  <div className="dropdown-items">
                    <button
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        setIsProfileDropdownOpen(false);
                        // TODO: Navigate to profile settings
                        console.log('Navigate to profile settings');
                      }}
                    >
                      <span className="item-icon">👤</span>
                      <span className="item-text">Profile Settings</span>
                    </button>

                    <button
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        setIsProfileDropdownOpen(false);
                        // TODO: Navigate to account settings
                        console.log('Navigate to account settings');
                      }}
                    >
                      <span className="item-icon">⚙️</span>
                      <span className="item-text">Account Settings</span>
                    </button>

                    <button
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        setIsProfileDropdownOpen(false);
                        // TODO: Navigate to help center
                        console.log('Navigate to help center');
                      }}
                    >
                      <span className="item-icon">❓</span>
                      <span className="item-text">Help Center</span>
                    </button>
                  </div>

                  <div className="dropdown-divider" />

                  <div className="dropdown-footer">
                    <button
                      className="dropdown-item logout-item"
                      role="menuitem"
                      onClick={handleLogout}
                    >
                      <span className="item-icon">🚪</span>
                      <span className="item-text">Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AuthenticatedHeader;